import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {CardModel} from '../../../../models/api/cardModel';
import {parseContactUid} from '../../../../models/api/contactModel';
import {CardAvatar} from '../../../common/avatars/cardAvatar';
import {OverflowWithCount} from '../../../common/overflow/overflowWithCount';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../common/skeleton';

/*
 * Props.
 */

interface AccountDetailsContactProps {
  card?: CardModel;
  isLoading?: boolean;
  openContactDetailsPopUp?: Handler;
}

/*
 * Style.
 */

const StyledContactContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  padding: 16px;
  border-radius: 8px;
  ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};
  border: 1px solid ${(p) => p.theme.alphas.black30};

  &:hover {
    background: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledDetailsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
  overflow: hidden;
  padding-left: 12px;
  vertical-align: middle;
`;

const StyledNameAndVisContainerDiv = styled.div`
  display: flex;
  overflow: hidden;
`;

const StyledNameContainerDiv = styled.div`
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade90};
  line-height: 16px;
  margin-bottom: 2px;
  ${ellipsis()}
`;

const StyledHandlesDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  display: flex;
  flex-flow: row;
`;

const StyledHandleDiv = styled.div`
  line-height: 14px;
  ${ellipsis()}
`;

const StyledOverflowDiv = styled.div`
  padding-left: 4px;

  &:not(:empty) {
    padding-left: 0;
  }
`;

const StyledHoverContainerDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 30px;
`;

const StyledPopoutIcon = styled(Icon)`
  margin-left: 12px;
  color: ${(p) => p.theme.greys.shade50};
`;

const StyledCardAvatar = styled(CardAvatar)`
  margin: 1px 0 0 1px;
`;

/*
 * Component.
 */

export const AccountDetailsContact: FC<AccountDetailsContactProps> = (props) => {
  const {card, isLoading, openContactDetailsPopUp} = props;
  const [isHovering, setIsHovering] = useState(false);

  if (isLoading) {
    return <Skeleton height="64px" borderRadius="8px" scheme={SkeletonColorSchemesEnum.GREY10} />;
  }
  if (!card) {
    return null;
  }

  const handles = card.contactUids.map((contactUid) => parseContactUid(contactUid)[1]);

  const renderElement = (handle: string, index: number) => (
    <StyledHandleDiv key={handle}>
      {handle}
      {index === handles.length - 1 ? '' : <>,&nbsp;</>}
    </StyledHandleDiv>
  );
  const renderOverflowIndicator = (overflowCount: number) => (
    <StyledOverflowDiv>+{overflowCount}</StyledOverflowDiv>
  );

  return (
    <StyledContactContainerDiv
      onMouseEnter={() => setIsHovering(true)}
      onMouseOver={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={() => {
        if (openContactDetailsPopUp) {
          openContactDetailsPopUp();
        }
      }}
    >
      <StyledCardAvatar card={card} size={VisualSizesEnum.LARGE} />
      <StyledDetailsContainerDiv>
        <StyledNameAndVisContainerDiv>
          <StyledNameContainerDiv>{card.name}</StyledNameContainerDiv>
        </StyledNameAndVisContainerDiv>
        <StyledHandlesDiv>
          <OverflowWithCount<string>
            elements={handles}
            renderElement={renderElement}
            renderOverflowIndicator={renderOverflowIndicator}
            shouldRenderPartial
          />
        </StyledHandlesDiv>
      </StyledDetailsContainerDiv>
      <StyledHoverContainerDiv>
        {isHovering && <StyledPopoutIcon name="popoutSmall" size={16} />}
      </StyledHoverContainerDiv>
    </StyledContactContainerDiv>
  );
};
